import { Fragment, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { canManagePurchaseOrders } from "@/lib/permissions";
import { PageHeader } from "@/components/base/PageHeader";
import { TableSkeleton } from "@/components/base/TableSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { money } from "@/lib/format";
import { getActiveCurrencyCode } from "@/lib/currency";
import {
  replenishmentStatusLabel,
  confidenceLabel,
  recommendationReasons,
} from "@/lib/replenishment";
import * as purchasingApi from "@/lib/api/purchasing";

interface ReplenishmentRow {
  product_id: string;
  variant_id: string | null;
  product_name: string;
  sku: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  supplier_sku: string | null;
  supplier_price: number | null;
  available: number;
  incoming: number;
  allocated: number;
  on_hand: number;
  daily_velocity: number | null;
  days_of_stock: number | null;
  reorder_point: number;
  reorder_qty: number;
  lead_time_days: number;
  lead_time_demand: number | null;
  moq: number;
  order_multiple: number;
  confidence: string;
  recommended_qty: number;
  status: string;
  location_id: string | null;
  location_name: string | null;
}

interface SelectedLine {
  product_id: string;
  variant_id: string | null;
  supplier_id: string | null;
  product_name: string;
  sku: string | null;
  supplier_name: string | null;
  supplier_sku: string | null;
  supplier_price: number | null;
  qty: number;
  unit_cost: number;
  order_multiple: number;
  moq: number;
}

const inputCls = "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

/** Confidence badge styling — mirrors the server `confidence` values. */
function confidenceBadgeClass(confidence: string): string {
  switch (confidence) {
    case "high": return "bg-emerald-100 text-emerald-700";
    case "medium": return "bg-amber-100 text-amber-700";
    case "no_velocity":
    case "no_data": return "bg-slate-100 text-slate-500";
    default: return "bg-red-100 text-red-700";
  }
}

export default function Replenishment() {
  const { org, role } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [poOpen, setPoOpen] = useState(false);
  const [poSaving, setPoSaving] = useState(false);
  const [poError, setPoError] = useState("");
  const [locationId, setLocationId] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState<"all" | "stockout" | "critical">("all");
  const [search, setSearch] = useState("");
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const rowKey = (r: ReplenishmentRow) => `${r.product_id}::${r.variant_id ?? ""}`;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["replenishment", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [plan, locations, suppliers, supplierProducts] = await Promise.all([
        supabase.rpc("get_replenishment_plan_v2", { p_org_id: org!.id }),
        supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name"),
        supabase.from("suppliers").select("id, name, lead_time_days").eq("org_id", org!.id).eq("status", "active").order("name"),
        supabase.from("supplier_products").select("supplier_id, product_id, variant_id, is_preferred").eq("org_id", org!.id).eq("is_preferred", true),
      ]);
      const prefByVariant = new Set<string>();
      const prefByProduct = new Set<string>();
      for (const sp of (supplierProducts.data ?? []) as { supplier_id: string; product_id: string; variant_id: string | null; is_preferred: boolean }[]) {
        if (sp.variant_id) prefByVariant.add(`${sp.supplier_id}|${sp.variant_id}`);
        else prefByProduct.add(`${sp.supplier_id}|p:${sp.product_id}`);
      }
      return {
        plan: (plan.data ?? []) as ReplenishmentRow[],
        locations: (locations.data ?? []) as { id: string; name: string }[],
        suppliers: (suppliers.data ?? []) as { id: string; name: string; lead_time_days: number | null }[],
        prefByVariant,
        prefByProduct,
      };
    },
  });

  const filteredPlan = useMemo(() => {
    if (!data) return [];
    return data.plan.filter((r) => {
      if (urgencyFilter === "stockout" && r.status !== 'stockout') return false;
      if (urgencyFilter === "critical" && r.status !== 'stockout' && r.status !== 'reorder') return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!(r.product_name.toLowerCase().includes(q) || (r.sku ?? "").toLowerCase().includes(q) || (r.supplier_name ?? "").toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [data, urgencyFilter, search]);

  const toggleRow = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    const selectable = filteredPlan.filter((r) => r.recommended_qty > 0);
    if (selectable.length > 0 && selected.size === selectable.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectable.map(rowKey)));
    }
  };

  const selectedRows = useMemo(() => {
    return filteredPlan.filter((r) => selected.has(rowKey(r)));
  }, [filteredPlan, selected]);

  const isPreferred = (r: ReplenishmentRow) => {
    if (!data || !r.supplier_id) return false;
    return data.prefByVariant.has(`${r.supplier_id}|${r.variant_id}`) || data.prefByProduct.has(`${r.supplier_id}|p:${r.product_id}`);
  };

  const selectableCount = useMemo(() => filteredPlan.filter((r) => r.recommended_qty > 0).length, [filteredPlan]);

  // Group by supplier for draft PO creation
  const bySupplier = useMemo(() => {
    const map: Record<string, { supplierId: string | null; supplierName: string; lines: SelectedLine[] }> = {};
    for (const r of selectedRows) {
      const key = r.supplier_id ?? "__none__";
      if (!map[key]) {
        map[key] = { supplierId: r.supplier_id, supplierName: r.supplier_name ?? "No supplier", lines: [] };
      }
      map[key].lines.push({
        product_id: r.product_id,
        variant_id: r.variant_id,
        supplier_id: r.supplier_id,
        product_name: r.product_name,
        sku: r.sku,
        supplier_name: r.supplier_name,
        supplier_sku: r.supplier_sku ?? null,
        supplier_price: r.supplier_price,
        qty: r.recommended_qty,
        unit_cost: Number(r.supplier_price ?? 0),
        order_multiple: r.order_multiple ?? 1,
        moq: r.moq ?? 1,
      });
    }
    return Object.values(map);
  }, [selectedRows]);

  const createDraftPos = async () => {
    if (!locationId) {
      setPoError("Select a receiving location.");
      return;
    }
    if (bySupplier.length === 0) {
      setPoError("No items selected.");
      return;
    }
    setPoSaving(true);
    setPoError("");
    let successCount = 0;
    const failedSuppliers: string[] = [];
    const poIds: string[] = [];

    try {
      for (const group of bySupplier) {
        if (!group.supplierId) continue; // Skip items without supplier — user must assign manually

        const pLines = group.lines.map((l) => ({
          product_id: l.product_id,
          variant_id: l.variant_id,
          qty: l.qty,
          unit_cost: l.unit_cost,
          supplier_sku: l.supplier_sku ?? null,
        }));

        let poId: string;
        try {
          poId = await purchasingApi.createPurchaseOrder({
            supplierId: group.supplierId,
            locationId,
            expectedDate: expectedDate || null,
            currency: getActiveCurrencyCode(),
            notes: null,
            supplierReference: null,
            shippingCost: 0,
            discount: 0,
            lines: pLines,
            idempotencyKey: crypto.randomUUID(),
          });
        } catch {
          failedSuppliers.push(group.supplierName);
          continue;
        }

        if (!poId) {
          failedSuppliers.push(group.supplierName);
          continue;
        }
        poIds.push(poId);
        successCount++;
      }

      if (successCount > 0) {
        queryClient.invalidateQueries({ queryKey: ["purchase-orders", org?.id] });
        queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });

        if (failedSuppliers.length > 0) {
          // Partial failure: some draft POs were created, some supplier groups failed.
          // Keep the modal open and report exactly what failed so the operator can retry.
          setPoError(
            `Created ${successCount} draft PO(s), but ${failedSuppliers.length} supplier group(s) failed: ${failedSuppliers.join(", ")}. ` +
            "Review the purchase order list and retry the failed items.",
          );
          setSelected(new Set());
        } else {
          setPoOpen(false);
          setSelected(new Set());
          if (poIds.length === 1) {
            navigate(`/purchase-orders/${poIds[0]}`);
          } else {
            navigate("/purchase-orders");
          }
        }
      } else {
        setPoError(
          failedSuppliers.length > 0
            ? `Could not create any purchase orders: ${failedSuppliers.join(", ")}.`
            : "Could not create purchase orders. Ensure all selected items have a supplier assigned.",
        );
      }
    } catch (e) {
      setPoError("An unexpected error occurred.");
    } finally {
      setPoSaving(false);
    }
  };

  const urgencyClass = (r: ReplenishmentRow) => {
    if (r.status === 'stockout' && r.incoming === 0) return "bg-red-50 border-l-4 border-l-red-500";
    if (r.status === 'stockout') return "bg-amber-50 border-l-4 border-l-amber-400";
    if (r.days_of_stock != null && r.days_of_stock <= 3) return "bg-red-50 border-l-4 border-l-red-400";
    if (r.days_of_stock != null && r.days_of_stock <= 7) return "bg-amber-50 border-l-4 border-l-amber-300";
    return "";
  };

  const canCreate = canManagePurchaseOrders(role);

  return (
    <div>
      <PageHeader title="Restock planning" subtitle="What to reorder, why, and from which supplier — from your live stock and sales">
        {canCreate && (
          <button
            onClick={() => { setPoError(""); setPoOpen(true); }}
            disabled={selected.size === 0}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-add-line" />
            Create draft order{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
        )}
      </PageHeader>

      {!canCreate && (
        <div className="mb-4 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 leading-relaxed">
          <i className="ri-eye-line mr-1" />
          You can view restock planning and choose what to order, but creating purchase orders requires the purchasing role. Ask an owner, admin, or manager to assign it.
        </div>
      )}

      {/* Mental model explanation */}
      <div className="mb-4 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-lg text-xs text-emerald-800 leading-relaxed">
        <i className="ri-information-line mr-1" />
        <strong>This is a planning list, not a place stock gets moved.</strong> A product appears here only when it has a reorder policy (a reorder point or reorder quantity) and is out of stock, below its reorder point, or projected to run out soon. A product with no reorder point or reorder quantity set is "not stocked" — it won't show here. Set a reorder point on the product to start managing its stock.
      </div>
      <div className="mb-5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 leading-relaxed">
        <i className="ri-user-star-line mr-1" />
        <strong>Supplier:</strong> each line uses your <strong>preferred</strong> supplier for that product when one is set; otherwise the lowest-price active supplier. A "Preferred" badge marks the chosen supplier.
      </div>

      {/* Urgency legend */}
      <div className="mb-5 flex flex-wrap items-center gap-4 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-red-500 inline-block" />
          Out of stock — not on order
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" />
          Out of stock — already on order
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-amber-300 inline-block" />
          Running low (≤ 7 days of stock)
        </div>
        <span className="text-slate-400 ml-auto">Based on your live stock levels and recent sales.</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 max-w-md w-full">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search product, SKU or supplier..."
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
            {(["all", "stockout", "critical"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setUrgencyFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  urgencyFilter === f ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {f === "all" ? "All" : f === "stockout" ? "Out of stock" : "Needs reorder"}
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-400 whitespace-nowrap">{filteredPlan.length} items need ordering</span>
        </div>

        {isLoading ? (
          <div className="p-8"><TableSkeleton /></div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-500 mb-4">Couldn't load replenishment data.</p>
            <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
          </div>
        ) : filteredPlan.length === 0 ? (
          <EmptyState icon="ri-checkbox-circle-line" title="All stocked up!" description="No products are below their reorder point right now." />
        ) : (
          <>
            {/* Desktop table — essential columns only */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-100">
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectableCount > 0 && selected.size === selectableCount}
                        onChange={toggleAll}
                        aria-label="Select all items"
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Supplier</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">In stock</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">On order</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order this</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlan.map((r) => {
                    const key = rowKey(r);
                    const isSelected = selected.has(key);
                    const target = r.reorder_point + r.reorder_qty;
                    const gap = target - r.available - r.incoming;
                    const reasons = recommendationReasons(r);
                    return (
                      <Fragment key={key}>
                        <tr className={`border-b border-slate-50 hover:bg-slate-50/40 transition-colors ${urgencyClass(r)}`}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={r.recommended_qty <= 0}
                              onChange={() => toggleRow(key)}
                              aria-label={`Select ${r.product_name}`}
                              className={`w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 ${r.recommended_qty <= 0 ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => setExpandedKey(expandedKey === key ? null : key)}
                                className={`w-5 h-5 shrink-0 flex items-center justify-center rounded hover:bg-slate-200/60 transition-colors cursor-pointer ${expandedKey === key ? "text-emerald-600" : "text-slate-400 hover:text-slate-600"}`}
                                title="Why is this quantity recommended?"
                                aria-label="Why is this quantity recommended?"
                                aria-expanded={expandedKey === key}
                              >
                                <i className="ri-question-line text-sm" />
                              </button>
                              <p className="text-sm font-medium text-slate-800">{r.product_name}</p>
                            </div>
                            <p className="text-xs text-slate-400 font-mono">{r.sku ?? "—"}</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {replenishmentStatusLabel(r.status)}
                              {(r.confidence === 'no_data' || r.confidence === 'no_velocity') && <span className="text-slate-400"> · no sales history yet</span>}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {r.supplier_name ? (
                              <span className="flex items-center gap-1.5">
                                {r.supplier_name}
                                {isPreferred(r) && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap" title="Preferred supplier — chosen first regardless of price">Preferred</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-amber-600 text-xs">No supplier</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-right">
                            <span className={r.available === 0 ? "font-bold text-red-600" : "text-slate-800"}>{r.available}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500 text-right font-medium">{r.incoming}</td>
                          <td className="px-4 py-3 text-right">
                            {r.recommended_qty > 0 ? (
                              <div>
                                <span className="text-sm font-bold text-emerald-700">{r.recommended_qty}</span>
                                {r.order_multiple > 1 && <span className="text-xs text-slate-400 ml-1">(×{r.order_multiple})</span>}
                                {reasons.length > 0 && (
                                  <p className="text-[11px] text-slate-400 mt-0.5 max-w-[180px] whitespace-normal text-right">{reasons.slice(0, 2).join(" · ")}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">
                                {r.incoming > 0 ? "Covered (on order)" : (r.reorder_point + r.reorder_qty) <= 0 ? "No reorder policy" : "—"}
                              </span>
                            )}
                          </td>
                        </tr>
                        {expandedKey === key && (
                          <tr className="border-b border-slate-50 bg-slate-50/50">
                            <td colSpan={6} className="px-6 py-4">
                              <ReplenishmentDetail r={r} target={target} gap={gap} velocity30={r.daily_velocity != null ? Math.round(Number(r.daily_velocity) * 30) : null} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards — same plan data, essential fields + expandable detail */}
            <div className="md:hidden divide-y divide-slate-100">
              {filteredPlan.map((r) => {
                const key = rowKey(r);
                const isSelected = selected.has(key);
                const target = r.reorder_point + r.reorder_qty;
                const gap = target - r.available - r.incoming;
                const reasons = recommendationReasons(r);
                const expanded = expandedKey === key;
                return (
                  <div key={key} className={`px-4 py-3 ${urgencyClass(r)}`}>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={r.recommended_qty <= 0}
                        onChange={() => toggleRow(key)}
                        aria-label={`Select ${r.product_name}`}
                        className={`mt-0.5 w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 ${r.recommended_qty <= 0 ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{r.product_name}</p>
                            <p className="text-xs text-slate-400 font-mono">{r.sku ?? "—"}</p>
                          </div>
                          <button
                            onClick={() => setExpandedKey(expanded ? null : key)}
                            className={`w-6 h-6 shrink-0 flex items-center justify-center rounded hover:bg-slate-200/60 transition-colors cursor-pointer ${expanded ? "text-emerald-600" : "text-slate-400 hover:text-slate-600"}`}
                            aria-label="Why is this quantity recommended?"
                            aria-expanded={expanded}
                          >
                            <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`} />
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{replenishmentStatusLabel(r.status)}</p>

                        <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                          <div className="bg-white border border-slate-200 rounded-lg py-1.5">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide">In stock</p>
                            <p className={`text-sm font-semibold ${r.available === 0 ? "text-red-600" : "text-slate-800"}`}>{r.available}</p>
                          </div>
                          <div className="bg-white border border-slate-200 rounded-lg py-1.5">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide">On order</p>
                            <p className="text-sm font-semibold text-slate-700">{r.incoming}</p>
                          </div>
                          <div className="bg-white border border-slate-200 rounded-lg py-1.5">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Order this</p>
                            <p className="text-sm font-bold text-emerald-700">
                              {r.recommended_qty > 0 ? r.recommended_qty : (r.incoming > 0 ? "—" : "—")}
                            </p>
                          </div>
                        </div>

                        <div className="mt-2 flex items-center justify-between text-xs">
                          <span className="text-slate-600">
                            {r.supplier_name ? (
                              <span className="flex items-center gap-1.5">
                                {r.supplier_name}
                                {isPreferred(r) && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Preferred</span>}
                              </span>
                            ) : (
                              <span className="text-amber-600">No supplier</span>
                            )}
                          </span>
                          {reasons.length > 0 && <span className="text-slate-400 truncate ml-2 text-right">{reasons[0]}</span>}
                        </div>
                      </div>
                    </div>

                    {expanded && (
                      <div className="mt-3">
                        <ReplenishmentDetail r={r} target={target} gap={gap} velocity30={r.daily_velocity != null ? Math.round(Number(r.daily_velocity) * 30) : null} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {filteredPlan.length > 0 && (
          <div className="px-4 py-3 bg-slate-50/60 border-t border-slate-100 text-xs text-slate-400">
            Recommended qty = order up to (reorder point + reorder qty), minus stock on hand and what's already on order — then rounded up to the supplier's minimum and pack size. "Covered" means what's on order already reaches the target.
          </div>
        )}
      </div>

      <Modal
        open={poOpen}
        onClose={() => setPoOpen(false)}
        title="Create draft purchase orders"
        footer={
          <>
            <button onClick={() => setPoOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={createDraftPos} disabled={poSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {poSaving ? "Creating..." : `Create ${bySupplier.filter((g) => g.supplierId).length} draft order(s)`}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {poError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{poError}</div>}
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-xs text-slate-600">
            <p className="font-semibold text-slate-700 mb-1">Draft purchase orders will be created as follows:</p>
            {bySupplier.length === 0 ? (
              <p className="text-amber-600">No valid supplier-product pairs selected.</p>
            ) : (
              bySupplier.map((g) => (
                <div key={g.supplierId ?? "none"} className="mb-2 last:mb-0">
                  <span className="font-medium text-slate-700">{g.supplierName}</span>
                  {!g.supplierId && <span className="text-amber-600 ml-1">(no supplier assigned — will be skipped)</span>}
                  <div className="ml-3 mt-1 space-y-0.5">
                    {g.lines.map((l, i) => (
                      <div key={i} className="flex items-center justify-between gap-3">
                        <span>{l.product_name} {l.sku ? `(${l.sku})` : ""}</span>
                        <span className="font-semibold text-slate-800">×{l.qty}</span>
                      </div>
                    ))}
                    <div className="text-right font-medium text-slate-700 mt-1">
                      Total: {money(g.lines.reduce((s, l) => s + l.qty * l.unit_cost, 0))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div>
            <label className={labelCls}>Receiving location *</label>
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className={inputCls}>
              <option value="">Select location...</option>
              {(data?.locations ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Expected delivery date</label>
            <input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} className={inputCls} />
          </div>
          <p className="text-xs text-slate-400">Draft orders are created for review — nothing is sent to a supplier automatically. You can edit quantities and mark the order as ordered after reviewing.</p>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Shared expandable detail for a replenishment line — desktop row expansion and
 * mobile card expansion render the exact same canonical data. Advanced planning
 * fields live here so the default view stays operator-friendly.
 */
function ReplenishmentDetail({
  r,
  target,
  gap,
  velocity30,
}: {
  r: ReplenishmentRow;
  target: number;
  gap: number;
  velocity30: number | null;
}) {
  const reasons = recommendationReasons(r);
  return (
    <div className="max-w-3xl space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Why this quantity is recommended</p>

      {reasons.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {reasons.map((reason) => (
            <span key={reason} className="text-xs px-2 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 font-medium">
              {reason}
            </span>
          ))}
        </div>
      )}

      {r.recommended_qty > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">Order-up-to target</p>
              <p className="font-semibold text-slate-800 mt-0.5">{target} units</p>
              <p className="text-xs text-slate-500 mt-1">Reorder point {r.reorder_point} + reorder qty {r.reorder_qty}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">In stock now</p>
              <p className="font-semibold text-slate-800 mt-0.5">{r.available} units</p>
              <p className="text-xs text-slate-500 mt-1">{r.on_hand} on hand · {r.allocated} reserved</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">Already on order</p>
              <p className="font-semibold text-slate-800 mt-0.5">{r.incoming} units</p>
              <p className="text-xs text-slate-500 mt-1">Committed open purchase orders</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">Sales pace</p>
              <p className="font-semibold text-slate-800 mt-0.5">{velocity30 != null ? `${velocity30} / 30d` : "No history"}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">Days of stock left</p>
              <p className={`font-semibold mt-0.5 ${r.days_of_stock != null && r.days_of_stock <= 7 ? "text-amber-700" : "text-slate-800"}`}>
                {r.days_of_stock != null ? `${r.days_of_stock}d` : "—"}
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">Lead time</p>
              <p className="font-semibold text-slate-800 mt-0.5">{r.lead_time_days > 0 ? `${r.lead_time_days}d` : "—"}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">Expected during lead time</p>
              <p className="font-semibold text-slate-800 mt-0.5">{r.lead_time_demand != null && Number(r.lead_time_demand) > 0 ? `${Number(r.lead_time_demand).toFixed(1)} units` : "—"}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">Minimum order</p>
              <p className="font-semibold text-slate-800 mt-0.5">{r.moq > 1 ? r.moq : "None"}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">Order in multiples of</p>
              <p className="font-semibold text-slate-800 mt-0.5">{r.order_multiple > 1 ? r.order_multiple : "Any"}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">Confidence</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${confidenceBadgeClass(r.confidence)}`}>
                {confidenceLabel(r.confidence)}
              </span>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-400">Unit cost</p>
              <p className="font-semibold text-slate-800 mt-0.5">{r.supplier_price != null ? money(r.supplier_price) : "—"}</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-3 text-sm">
            <p className="text-xs text-slate-400 mb-1">Supplier</p>
            <p className="font-semibold text-slate-800">{r.supplier_name ?? "No supplier"}</p>
            {r.supplier_sku && <p className="text-xs text-slate-500 mt-0.5 font-mono">{r.supplier_sku}</p>}
          </div>

          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-sm">
            <p className="text-emerald-800">
              <span className="font-semibold">Shortfall: {target} − {r.available} (in stock) − {r.incoming} (on order) = {gap}.</span>{" "}
              Adjusted for a minimum order of {r.moq}{r.order_multiple > 1 ? ` and rounded up to multiples of ${r.order_multiple}` : ""} → <span className="font-bold">{r.recommended_qty} units.</span>
            </p>
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-600">
          {r.incoming > 0
            ? `Already covered — ${r.incoming} units on order reach the ${target}-unit target. No new order needed.`
            : "No reorder policy set — add a reorder point and reorder quantity to start getting recommendations."}
        </p>
      )}
    </div>
  );
}